// Admin > Email health tab — Resend delivery/bounce/complaint reputation
// dashboard plus a "recent auto opt-outs" list.
import { RefreshCw } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { SectionTitle } from "@/components/app/Bits";
import { fmt } from "./_helpers";

export default function EmailHealthTab({ emailHealth, recentOptouts, loadEmailHealth }) {
  return (
    <div className="space-y-6" data-testid="admin-email-health">
      <div className="flex items-center justify-between">
        <SectionTitle overline="Sender reputation" title={`Last ${emailHealth?.window_days || 7} days`} />
        <Button variant="outline" size="sm" onClick={loadEmailHealth} data-testid="email-health-refresh">
          <RefreshCw size={14} className="mr-1.5" /> Refresh
        </Button>
      </div>

      {!emailHealth ? (
        <p className="text-sm text-muted-foreground">Loading email events…</p>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="email-health-buckets">
            {[
              { key: "delivered", label: "Delivered", value: emailHealth.buckets.delivered, tone: "good" },
              { key: "opened", label: "Opened", value: emailHealth.buckets.opened, tone: "good" },
              { key: "clicked", label: "Clicked", value: emailHealth.buckets.clicked, tone: "good" },
              { key: "sent", label: "Sent", value: emailHealth.buckets.sent, tone: "neutral" },
              { key: "bounced_hard", label: "Hard bounces", value: emailHealth.buckets.bounced_hard, tone: "bad" },
              { key: "bounced_soft", label: "Soft bounces", value: emailHealth.buckets.bounced_soft, tone: "warn" },
              { key: "complained", label: "Spam complaints", value: emailHealth.buckets.complained, tone: "bad" },
            ].map((b) => {
              const toneCls =
                b.tone === "good" ? "text-primary" :
                b.tone === "bad" ? "text-destructive" :
                b.tone === "warn" ? "text-[#CC9C33]" :
                "text-foreground";
              return (
                <Card key={b.key} className="border-border shadow-none rounded-lg" data-testid={`email-bucket-${b.key}`}>
                  <CardContent className="p-5">
                    <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{b.label}</p>
                    <p className={`font-display text-3xl font-extrabold tracking-tighter mt-2 ${toneCls}`} data-testid={`email-bucket-${b.key}-value`}>
                      {b.value}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <Card className="border-border shadow-none rounded-lg" data-testid="email-health-rates">
            <CardContent className="p-5">
              <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-3">Rolling rates</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Bounce rate</p>
                  <p className={`text-2xl font-bold mt-1 ${emailHealth.rates.bounce_pct > 5 ? "text-destructive" : "text-foreground"}`} data-testid="email-rate-bounce">
                    {emailHealth.rates.bounce_pct}%
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">Healthy: under 5%</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Complaint rate</p>
                  <p className={`text-2xl font-bold mt-1 ${emailHealth.rates.complaint_pct > 0.1 ? "text-destructive" : "text-foreground"}`} data-testid="email-rate-complaint">
                    {emailHealth.rates.complaint_pct}%
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">Healthy: under 0.1%</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Open rate</p>
                  <p className="text-2xl font-bold mt-1" data-testid="email-rate-open">
                    {emailHealth.rates.open_pct}%
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">Industry avg ~20%</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      <div>
        <SectionTitle overline="Auto opt-outs" title={`Recent ${recentOptouts.length}`} />
        <Card className="border-border shadow-none rounded-lg" data-testid="recent-optouts-card">
          <CardContent className="p-0">
            {recentOptouts.length === 0 ? (
              <p className="p-6 text-sm text-muted-foreground" data-testid="recent-optouts-empty">
                No auto opt-outs yet. When Resend reports a hard bounce or spam complaint, the affected address is added here automatically.
              </p>
            ) : (
              <ul className="divide-y divide-border">
                {recentOptouts.map((u) => (
                  <li key={u.email} className="px-5 py-3 flex items-center justify-between gap-3" data-testid={`optout-row-${u.email}`}>
                    <div className="min-w-0">
                      <p className="font-medium truncate" data-testid={`optout-email-${u.email}`}>{u.email}</p>
                      {u.name && <p className="text-xs text-muted-foreground truncate">{u.name}</p>}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span
                        className={`text-[11px] font-medium uppercase tracking-wide rounded px-2 py-0.5 ${
                          u.emails_opted_out_reason === "spam_complaint"
                            ? "bg-destructive/10 text-destructive"
                            : "bg-secondary text-secondary-foreground"
                        }`}
                        data-testid={`optout-reason-${u.email}`}
                      >
                        {u.emails_opted_out_reason === "spam_complaint" ? "Complaint" :
                          u.emails_opted_out_reason === "hard_bounce" ? "Hard bounce" :
                          u.emails_opted_out_reason || "Unsubscribed"}
                      </span>
                      {u.emails_opted_out_at && (
                        <span className="text-xs text-muted-foreground tabular-nums">{fmt(u.emails_opted_out_at)}</span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
